import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../ProgressBar';
import GroupRoleInfo from './GroupRoleInfo';
import PersonalInfo from './PersonalInfo';
import Preferences from './Preferences';
import HealthInfo from './HealthInfo'
import EmergencyContact from './EmergencyContact';
import { useSelector, useDispatch } from 'react-redux';
import useAxios from "../../services/AxiosConfig";
import styles from '../../views/ManagementPages.module.css';
import { openModal } from '../PerinHealthModelSlice';
import { closeAlert, showAlert } from '../PerinHealthAlertSlice';
import './Accounts.css';
import { setFormField } from './RegistrationSlice';
import { setDirty } from "../../slicers/DirtyCheckSlice";
import { Form, Modal, Button } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import modalStyles from '../PerinHealthModel.module.css';
import { cleanupRequestBody } from '../../utils/accountUtils';


const CreateAccount = () => {

    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const { isAdmin, groups } = useSelector((state) => state.userInfo);
    const httpService = useAxios(axiosConfig.envURL);
    const mirthAxios = useAxios("https://mirth.perinhealth.com/api/");
    const userData = useSelector((state) => state.registration);
    const updating = userData.updating;
    const selectedRole = userData.role.category;
    const roleName = userData.role.name;
    const [profileFile, setProfileFile] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [patientLocations, setPatientLocations] = useState([]);

    const initOrg = (updating && userData.groups.length !== 0) ? userData.groups[0].organizationId : 0;
    const [selectedOrganization, setSelectedOrganization] = useState(initOrg);
    const navigate = useNavigate();
    const [page, setPage] = useState(0);

    const [validated, setValidated] = useState(false);
    const formRef = useRef(null);

    const getPatientLocations = async () => {
        if (userData.patient_id != null && userData.ehrAccount) {
            try {
                if (isAdmin) {
                    // Fetch data for all organizations in parallel
                    const organizationPromises = groups.map(async (group) => {
                        const response = await mirthAxios.get(`${group.organizationId}/patients/locations`);
                        const patients = Array.isArray(response.data) ? response.data.map((item) => ({ ...item, organizationId: group.organizationId })) : [];
                        return [...patients];
                    });
                    
                    const allUsersArrays = await Promise.all(organizationPromises);
                    const allUsers = allUsersArrays.flat();
                    setPatientLocations(allUsers);
                } else {
                    const response = await mirthAxios.get(`${userData.ehrOrgId}/patients/locations`);
                    const patients = Array.isArray(response.data) ? response.data : [];
                    setPatientLocations(patients);
                }
            } catch (err) {
                console.error('Error fetching data:', err);
            }
        }
    };

    const checkPageValidityCombined = () => {
        let hasCustomErrors = checkPageValidity();
        if (formRef.current) {
            let formIsInvalid = formRef.current.checkValidity() === false;
            setValidated(formIsInvalid); 
            if (page === 1) {
                setErrorMessages((prevErrors) => ({
                    ...prevErrors,
                    personalInfo: {
                        ...prevErrors.personalInfo,
                        formInvalid: formIsInvalid,
                    },
                }));
            } else if (page === 2 && selectedRole === "Patient") {
                setErrorMessages((prevErrors) => ({
                    ...prevErrors,
                    healthInfo: formIsInvalid, 
                }));
            }
            return hasCustomErrors || formIsInvalid; 
        }
        return hasCustomErrors; 
    };

    useEffect(() => {
        const handleKeydown = (event) => {
            dispatch(setDirty());
        };
        document.addEventListener('keydown', handleKeydown);
        return () => {
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [dispatch]);

    useEffect(() => {
        getPatientLocations();
    }, []);

    const [errorMessages, setErrorMessages] = useState({
        groupRoleInfo: {
            groupsMessage: "Please assign groups",
            groupsShowing: false,
            employeeShowing: false
        },
        personalInfo: {
            countryCodeShowing: false,
            formInvalid: false
        },
        healthInfo: false,
        emergencyContact: false,
    });

    const handleProfileDrop = (acceptedFiles) => {
        const logoFile = acceptedFiles[0];
        if (logoFile) {
            setProfileFile(logoFile);
            const objectURL = URL.createObjectURL(logoFile);
            dispatch(setFormField({ key: "profilePictureURL", value: objectURL }));
        }
    };

    const PageDisplay = () => {
        if (page === 0) {
            return <GroupRoleInfo
                selectedOrganization={selectedOrganization}
                setSelectedOrganization={setSelectedOrganization}
                errors={errorMessages.groupRoleInfo}
                setProfileFile={handleProfileDrop}
            />;
        } else if (page === 1) {
            return <PersonalInfo errors={errorMessages.personalInfo} validated={validated} />;
        } else if (page === 2 && selectedRole === "Patient") {
            return <HealthInfo />;
        } else if (page === 2 && selectedRole !== "Patient") {
            return <Preferences />;
        } else if (page === 3 && selectedRole === "Patient") {
            return <EmergencyContact errors={errorMessages.emergencyContact} validated={validated}/>;
        }
        else {
            navigate('/account-management');
        }
    };

    const getPageName = (step) => {
        if (step === 0) {
            return "Group & Role Info";
        } else if (step === 1) {
            return "Personal Info";
        } else if (step === 2 && selectedRole === "Patient") {
            return "Health Info";
        } else if (step === 2 && selectedRole !== "Patient") {
            return "Preferences";
        } else if (step === 3 && selectedRole === "Patient") {
            return "Emergency Contact";
        }
    };

    const ProgressBarNavigation = (status) => {
        const pages = {
            "Group & Role Info": 0,
            "Personal Info": 1,
            "Health Info": 2,
            "Preferences": 2,
            "Emergency Contact": 3,
        };
        if (pages[status] || pages[status] === 0) {
            setPage(pages[status]);
        } else {
            return;
        }
    };

    const lastPage = () => {
        if ((selectedRole === "Patient" && page === 3) || (selectedRole !== "Patient" && page === 2)) {
            return true;
        } else {
            return false;
        }
    };

    const nextPage = () => {
        if (selectedRole === "Patient" && page < 3) {
            setPage(page + 1);
        } else if (selectedRole !== "Patient" && page < 2) {
            setPage(page + 1);
        }
    };

    const prevPage = () => {
        if (page !== 0) {
            setPage(page - 1);
        }
    };

    const checkPageValidity = () => {
        let tempError = { ...errorMessages };
        if (page === 0) {
            let employeeInvalid = false;
            let groupsInvalid = false;
            if (userData.groups.length === 0) {
                groupsInvalid = true
            }
            if ((userData.employeeId === "" && userData.npi === "") && selectedRole !== "Patient") {
                employeeInvalid = true;
            }
            tempError.groupRoleInfo.employeeShowing = employeeInvalid;
            tempError.groupRoleInfo.groupsShowing = groupsInvalid;
            setErrorMessages(tempError);
            return employeeInvalid || groupsInvalid;
        } else if (page === 1) {
            let tempError = { ...errorMessages };
            tempError.personalInfo.countryCodeShowing = userData.countryCode === "";
            setErrorMessages(tempError);
            return tempError.personalInfo.countryCodeShowing;
        } else if (page === 3 && selectedRole === "Patient") {
            // Check if user has filled out either name or address, then require either email or phone
            if ((userData.emergencyContact.fullName !== '' || userData.emergencyContact.address1 !== '') && 
                (userData.emergencyContact.email === '' && userData.emergencyContact.phone === '')) {
                tempError.emergencyContact = true;
            } else if (userData.emergencyContact.phone !== '' && userData.emergencyContact.countryCode === '') {
                // If phone is provided but no country code, that's also an error
                tempError.emergencyContact = true;
            } else {
                tempError.emergencyContact = false;
            }
            setErrorMessages(tempError);
            return tempError.emergencyContact;
        } else {
            return false;
        }
    };

    const handleSubmit = async () => {
        const method = updating ? 'PUT' : 'POST';
        const url = updating ? `user/${userData.id}` : 'registration';
        const submissionData = cleanupRequestBody(userData, patientLocations);
        try {
            const response = await httpService.request({
                method: method,
                headers: { "Content-Type": "application/json" },
                url: url,
                data: JSON.stringify(submissionData),
            });

            const formData = new FormData();
            formData.append('file', profileFile);
            if (response) {
                if (profileFile) {
                    await httpService.request({
                        method: 'PUT',
                        url: `user/profilePicture/${updating ? userData.id : response.data.data.id}`,
                        data: formData,
                    });
                }
                if (updating) {
                    dispatch(
                        showAlert({
                            header: "Success",
                            message: "Updated account successfully",
                            type: 'success',
                        })
                    );
                } else {
                  
                    dispatch(closeAlert())
                    if (userData.ehrAccount) {
                        dispatch(
                            showAlert({
                                header: "Success",
                                message: "Account created successfully",
                                type: 'success',
                            })
                        );
                    } else {
                        dispatch(
                            openModal({
                                activeModalId: "CreateAccount",
                                header: "Account Created Successfully",
                                message: "Check your email to activate the account",
                                buttons: [
                                    { label: 'Ok', variant: 'primary', onClickKey: 'close' },
                                ],
                            })
                        );
                    }

                }

                if (userData.patient_id != null && userData.ehrAccount){
                    const orgId = userData.groups[0]?.organizationId;
                    await mirthAxios.request({
                        method: 'PATCH',
                        url: `${orgId}/patients/${userData.patient_id}`,
                        data: {
                        perin_user_id: response.data.data.id,
                        },
                    });
                }
            }
            if (userData.ehrAccount) {
                navigate('/ehr-accounts');
            } else {
                navigate('/account-management');
            }
        } catch (error) {
            let errorMessage = error.response?.data?.message ?? "Unknown Error";
            if (error.response?.data?.message?.includes("duplicate key value violates unique constraint \"unique_phone_number\"")) {
                errorMessage = "Phone number is already associated with an account."
            }
            if (error.response?.data?.message?.includes("User already exists in this role and group")) {
                errorMessage = "Email is already associated with an account."
            }
            dispatch(
                showAlert({
                    header: `Error ${updating ? "updating" : "creating"} account`,
                    message: errorMessage,
                    type: 'danger',
                    dismissable: true
                })
            );
        }
    };

    const getErrorMessage = () => {
        if (page === 0) {
            if (errorMessages.groupRoleInfo.groupsShowing || errorMessages.groupRoleInfo.employeeShowing) {
                return selectedRole === "Patient" 
                    ? "Group Assignment is required to proceed." 
                    : selectedRole === "Doctor" 
                        ? "Group Assignment and valid NPI are required to proceed."
                        : "Group Assignment and Employee Id are required to proceed.";
            }
        } else if (page === 1 && (errorMessages.personalInfo.formInvalid || errorMessages.personalInfo.countryCodeShowing)) {
            return "First Name, Last Name, Email, Country Code, and Phone Number are required to proceed.";
        } else if (page === 2 && selectedRole === "Patient" && errorMessages.healthInfo) {
            return "Date of Birth, Gender, and Ethnicity are Required to Proceed.";
        } else if (page === 3 && selectedRole === "Patient" && errorMessages.emergencyContact) {
            if (userData.emergencyContact.phone !== '' && userData.emergencyContact.countryCode === '') {
                return "Please select a country code for the emergency contact phone number.";
            }
            return "Please provide either an email or phone number for the emergency contact.";
        }
        return null;
    };

    const errorMessage = getErrorMessage();

    return (
        <div className={styles.MainCard}>
            <div className={styles.ContentTitle}>
                {updating ? "Update Account" : "Create Account"} - {roleName}
            </div>
            <div className={styles.ProgressBarContainer}>
                <ProgressBar page={page} navigateToStep={ProgressBarNavigation} getPageName={getPageName} totalSteps={selectedRole === "Patient" ? 4 : 3} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", overflow: "auto", flex: "1" }}>
                <div className={styles.HideScrollContainer}>
                    <Form ref={formRef} noValidate validated={validated}>
                        {PageDisplay()}
                    </Form>
                </div>
                <div className={styles.ButtonsFooterContainerWithError}>
                    {errorMessage && (
                        <div className="page-error">
                            <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="sm" style={{ color: "red" }} />
                            {errorMessage}
                        </div>
                    )}
                    <div style={{ display: "flex", columnGap: "10px" }}>
                        {page !== 0 ? (
                            <button
                                className={styles.BackButton}
                                onClick={(e) => {
                                    e.preventDefault();
                                    prevPage();
                                }}
                            >
                                Back
                            </button>)
                            : null}
                        <button
                            className={lastPage() || !updating ? styles.CreateButton : styles.NextUpdateButton}
                            onClick={(e) => {
                                e.preventDefault()
                                if (checkPageValidityCombined()) {
                                    return;
                                };
                                if (lastPage()) {
                                    updating ? handleSubmit() : setShowConfirmationModal(true)
                                } else {
                                    nextPage();
                                }
                            }}
                        >
                            {lastPage() && updating ? "Update Account" : lastPage() ? "Create Account" : "Next"}
                        </button>
                        {updating && !lastPage() && (
                            <button
                                style={{ float: "right", marginLeft: "auto" }}
                                className={styles.CreateButton}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (checkPageValidityCombined()) {
                                        return;
                                    }
                                    handleSubmit();
                                }}
                            >
                                Update
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <Modal
                show={showConfirmationModal}
                onHide={() => setShowConfirmationModal(false)}
                backdrop="static"
            >
                <Modal.Header>
                    <Modal.Title>
                    Confirm Information
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{fontSize:"18px"}}>Please confirm the information below before creating the account.</div>
                    <hr></hr>
                    <div style={{fontSize:"18px"}}>Email: <b>{userData.email}</b></div>
                    <div style={{fontSize:"18px"}}>Phone: <b>{userData.countryCode}{userData.phone}</b></div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        onClick={() => {
                            setShowConfirmationModal(false)
                            setPage(1)
                        }} 
                        className={modalStyles.SecondaryButton}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => {
                            setShowConfirmationModal(false)
                            handleSubmit()}
                        } 
                        className={modalStyles.ModelButton}
                    >
                        Create
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default CreateAccount;

